import { useState, forwardRef, useImperativeHandle } from 'react';
import { usePortfolio, api } from '../App';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Building2, Plus, Key } from 'lucide-react';

const PortfolioSelector = forwardRef(function PortfolioSelector({ userRole = 'agent' }, ref) {
  const { activePortfolioId, setActivePortfolioId, portfolios, refreshPortfolios } = usePortfolio();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [newPortfolioName, setNewPortfolioName] = useState('');
  const [joinToken, setJoinToken] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const activePortfolio = portfolios.find(p => p.id === activePortfolioId);

  useImperativeHandle(ref, () => ({
    openCreate: () => setShowCreateModal(true),
    openJoin: () => setShowJoinModal(true),
  }));

  const handleCreatePortfolio = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await api.post('/portfolios', { name: newPortfolioName || 'New Portfolio' });
      toast.success(`Portfolio "${response.data.name}" created!`);
      setShowCreateModal(false);
      setNewPortfolioName('');
      await refreshPortfolios();
      setActivePortfolioId(response.data.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create portfolio');
    } finally {
      setIsLoading(false);
    }
  };

  const handleJoinPortfolio = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await api.post('/portfolios/add-by-token', { token: joinToken });
      toast.success(`Joined portfolio "${response.data.name}"!`);
      setShowJoinModal(false);
      setJoinToken('');
      await refreshPortfolios();
      setActivePortfolioId(response.data.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid token or already a member');
    } finally {
      setIsLoading(false);
    }
  };

  // Always render the component - even with no portfolios, users need the Join button
  return (
    <div className="portfolio-selector" data-testid="portfolio-selector">
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <Building2 size={18} style={{ color: '#64748b' }} />
        
        {portfolios.length > 0 ? (
          <Select value={activePortfolioId || ''} onValueChange={setActivePortfolioId}>
            <SelectTrigger 
              className="portfolio-select-trigger"
              data-testid="portfolio-select"
            >
              <SelectValue placeholder="Select portfolio">
                {activePortfolio?.name || 'Select portfolio'}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {portfolios.map((portfolio) => (
                <SelectItem key={portfolio.id} value={portfolio.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <span>{portfolio.name}</span>
                  <span style={{ fontSize: '12px', color: '#94a3b8', marginLeft: '8px' }}>
                    {portfolio.unit_count} units
                  </span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        ) : (
          <span style={{ fontSize: '14px', color: '#64748b', fontStyle: 'italic' }}>
            No portfolios yet
          </span>
        )}

        {/* Owner can create new portfolios */}
        {userRole === 'owner' && (
          <button
            onClick={() => setShowCreateModal(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '8px 12px',
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '500'
            }}
            data-testid="create-portfolio-btn"
            className="portfolio-action-btn"
          >
            <Plus size={16} />
            New
          </button>
        )}

        {/* Anyone can join a portfolio with a token */}
        <button
          onClick={() => setShowJoinModal(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '8px 12px',
            background: '#f1f5f9',
            color: '#475569',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '500'
          }}
          data-testid="join-portfolio-btn"
          className="portfolio-action-btn"
        >
          <Key size={14} />
          Join
        </button>
      </div>

      {/* Create Portfolio Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent data-testid="create-portfolio-modal">
          <DialogHeader>
            <DialogTitle>Create New Portfolio</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreatePortfolio}>
            <div style={{ padding: '20px 0' }}>
              <div className="form-group">
                <Label htmlFor="portfolioName">Portfolio Name</Label>
                <Input
                  id="portfolioName"
                  type="text"
                  value={newPortfolioName}
                  onChange={(e) => setNewPortfolioName(e.target.value)}
                  placeholder="e.g., Downtown Properties"
                  data-testid="portfolio-name-input"
                />
              </div>
              <p style={{ marginTop: '12px', fontSize: '13px', color: '#64748b' }}>
                After creating, you can configure a Google Sheet and invite users.
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading} data-testid="confirm-create-portfolio">
                {isLoading ? 'Creating...' : 'Create Portfolio'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Join Portfolio Modal */}
      <Dialog open={showJoinModal} onOpenChange={setShowJoinModal}>
        <DialogContent data-testid="join-portfolio-modal">
          <DialogHeader>
            <DialogTitle>Join a Portfolio</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleJoinPortfolio}>
            <div style={{ padding: '20px 0' }}>
              <div className="form-group">
                <Label htmlFor="joinToken">Portfolio Token</Label>
                <Input
                  id="joinToken"
                  type="text"
                  value={joinToken}
                  onChange={(e) => setJoinToken(e.target.value.toUpperCase())}
                  placeholder="e.g., ABC12345"
                  maxLength={8}
                  style={{ 
                    textTransform: 'uppercase',
                    letterSpacing: '2px',
                    fontFamily: 'monospace',
                    fontSize: '18px',
                    textAlign: 'center'
                  }}
                  required
                  data-testid="join-token-input"
                />
              </div>
              <p style={{ marginTop: '12px', fontSize: '13px', color: '#64748b' }}>
                Enter the 8-character token provided by the portfolio owner.
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowJoinModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading || joinToken.length !== 8} data-testid="confirm-join-portfolio">
                {isLoading ? 'Joining...' : 'Join Portfolio'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
});

export default PortfolioSelector;
